import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, ChevronRightIcon } from 'lucide-react';
interface NavSection {
  id: string;
  label: string;
  group: string;
}
const sections: NavSection[] = [{
  id: 'overview',
  label: 'Overview',
  group: 'The Experience'
}, {
  id: 'guest-journey',
  label: 'Guest Journey',
  group: 'The Experience'
}, {
  id: 'five-portals',
  label: 'Five Portals',
  group: 'The Experience'
}, {
  id: 'story-arc',
  label: 'Story Arc',
  group: 'The Experience'
}, {
  id: 'floor-map',
  label: 'Floor Map',
  group: 'The Experience'
}, {
  id: 'final-reveal',
  label: 'Final Reveal',
  group: 'The Experience'
}, {
  id: 'food-drink',
  label: 'Food & Drink',
  group: 'Sensory Design'
}, {
  id: 'music-sound',
  label: 'Music & Sound',
  group: 'Sensory Design'
}, {
  id: 'aroma-scent',
  label: 'Aroma / Scent',
  group: 'Sensory Design'
}, {
  id: 'production',
  label: 'Production & Gantt',
  group: 'Production'
}, {
  id: 'sponsors',
  label: 'Sponsors',
  group: 'Stakeholders'
}, {
  id: 'ontario-tourism',
  label: 'Ontario Tourism',
  group: 'Stakeholders'
}, {
  id: 'gurdwaras',
  label: 'Gurdwaras',
  group: 'Stakeholders'
}, {
  id: 'previous-sponsors',
  label: 'Previous Sponsors',
  group: 'Stakeholders'
}, {
  id: 'schools',
  label: 'Schools',
  group: 'Stakeholders'
}, {
  id: 'wedding-planners',
  label: 'Wedding Planners',
  group: 'Stakeholders'
}, {
  id: 'resort-sales',
  label: 'Resort Sales',
  group: 'Stakeholders'
}, {
  id: 'investors',
  label: 'Investors',
  group: 'Stakeholders'
}, {
  id: 'downloads',
  label: 'Downloads',
  group: 'Resources'
}];
const groups = ['The Experience', 'Sensory Design', 'Production', 'Stakeholders', 'Resources'];
export function BlueprintNav() {
  const [activeSection, setActiveSection] = useState('overview');
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      const visible = entries.filter(e => e.isIntersecting);
      if (visible.length > 0) {
        const sorted = visible.sort((a, b) => {
          const aRect = a.boundingClientRect;
          const bRect = b.boundingClientRect;
          return Math.abs(aRect.top) - Math.abs(bRect.top);
        });
        setActiveSection(sorted[0].target.id);
      }
    }, {
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    });
    const sectionEls = sections.map(s => document.getElementById(s.id)).filter(Boolean);
    sectionEls.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({
        top,
        behavior: 'smooth'
      });
    }
    setMobileOpen(false);
  };
  return <>
      {/* Desktop sidebar */}
      <nav className="hidden xl:block fixed left-0 top-1/2 -translate-y-1/2 z-30 w-56 pl-5 pr-3" data-id="element-329">
        <div className="bg-[#0f0f23]/90 backdrop-blur-xl border border-[#d4a017]/10 rounded-lg py-4 px-3 max-h-[80vh] overflow-y-auto scrollbar-thin" data-id="element-330">
          {groups.map(group => <div key={group} className="mb-3" data-id="element-331">
              <p className="text-[#c2a878]/50 text-[10px] tracking-[0.2em] uppercase font-medium px-2 mb-1.5" data-id="element-332">
                {group}
              </p>
              {sections.filter(s => s.group === group).map(section => <button key={section.id} onClick={() => scrollTo(section.id)} className={`w-full text-left px-2 py-1.5 rounded text-xs transition-all duration-300 block ${activeSection === section.id ? 'text-[#d4a017] bg-[#d4a017]/10 font-medium' : 'text-[#f5f0e8]/40 hover:text-[#f5f0e8]/70 hover:bg-white/5'}`} data-id="element-333">
                    {section.label}
                  </button>)}
            </div>)}
        </div>
      </nav>

      {/* Mobile floating button */}
      <button onClick={() => setMobileOpen(true)} className="xl:hidden fixed bottom-6 right-6 z-40 w-12 h-12 bg-[#d4a017] text-black rounded-full flex items-center justify-center shadow-lg shadow-[#d4a017]/20" aria-label="Open section navigation" data-id="element-334">
        <MenuIcon className="w-5 h-5" data-id="element-335" />
      </button>

      {/* Mobile drawer */}
      <AnimatePresence data-id="element-336">
        {mobileOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="xl:hidden fixed inset-0 bg-black/80 backdrop-blur-sm z-50" onClick={() => setMobileOpen(false)} data-id="element-337" />
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className="xl:hidden fixed right-0 top-0 bottom-0 w-72 bg-[#0f0f23] border-l border-[#d4a017]/10 z-50 overflow-y-auto" data-id="element-338">
              <div className="flex items-center justify-between p-5 border-b border-white/5" data-id="element-339">
                <p className="text-[#d4a017] text-sm tracking-[0.15em] uppercase font-medium" data-id="element-340">
                  Sections
                </p>
                <button onClick={() => setMobileOpen(false)} className="text-white/40 hover:text-white" data-id="element-341">
                  <XIcon className="w-5 h-5" data-id="element-342" />
                </button>
              </div>
              <div className="p-4" data-id="element-343">
                {groups.map(group => <div key={group} className="mb-4" data-id="element-344">
                    <p className="text-[#c2a878]/50 text-[10px] tracking-[0.2em] uppercase font-medium px-2 mb-2" data-id="element-345">
                      {group}
                    </p>
                    {sections.filter(s => s.group === group).map(section => <button key={section.id} onClick={() => scrollTo(section.id)} className={`w-full text-left px-3 py-2.5 rounded text-sm transition-all duration-200 flex items-center justify-between ${activeSection === section.id ? 'text-[#d4a017] bg-[#d4a017]/10' : 'text-[#f5f0e8]/50 hover:text-[#f5f0e8]/80 hover:bg-white/5'}`} data-id="element-346">
                          {section.label}
                          {activeSection === section.id && <ChevronRightIcon className="w-3 h-3 text-[#d4a017]" data-id="element-347" />}
                        </button>)}
                  </div>)}
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}